import React, { useEffect, useRef, useState } from 'react';
import { isPermanentPeriod } from '../../constants/groupPeriods';
import { formatPeriodDateRange } from '../../utils/formatPeriodDates';

function periodTitle(period) {
  return period?.title || period?.code || 'Period';
}

function periodMeta(period) {
  if (!period) return '';
  if (isPermanentPeriod(period)) return 'Permanent · open-ended';
  const range = formatPeriodDateRange(period.start_date, period.end_date);
  const code = period.code && period.code !== period.title ? period.code : '';
  if (range && code) return `${code} · ${range}`;
  return range || code || '';
}

/**
 * Badge / card period picker (replaces a plain <select>).
 */
const GroupsPeriodBadgePicker = ({
  id = 'create-entity-period',
  periods = [],
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select a period…',
  required = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = (periods || []).find((p) => String(p.id) === String(value)) || null;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const empty = !periods?.length;

  return (
    <div className="groups-period-badge-picker" ref={wrapRef}>
      {/* Keep a hidden input so native form required validation still works */}
      <input
        type="text"
        id={id}
        value={value || ''}
        required={required && !empty}
        tabIndex={-1}
        aria-hidden
        readOnly
        className="groups-period-badge-picker-sr"
      />
      <button
        type="button"
        className={`groups-period-badge-trigger${menuOpen ? ' is-open' : ''}${
          selected ? ' has-value' : ''
        }${isPermanentPeriod(selected) ? ' is-permanent' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        aria-labelledby={`${id}-label`}
        onClick={() => !disabled && !empty && setMenuOpen((o) => !o)}
        disabled={disabled || empty}
      >
        <span className="groups-period-badge-trigger-main">
          <span className="groups-period-badge-trigger-title">
            {selected ? periodTitle(selected) : placeholder}
          </span>
          {selected ? (
            <span className="groups-period-badge-trigger-meta">{periodMeta(selected)}</span>
          ) : null}
        </span>
        <span className="groups-period-badge-caret" aria-hidden>
          ▾
        </span>
      </button>

      {menuOpen ? (
        <ul className="groups-period-badge-menu" role="listbox" aria-labelledby={`${id}-label`}>
          {periods.map((period) => {
            const active = String(period.id) === String(value);
            const permanent = isPermanentPeriod(period);
            return (
              <li key={period.id} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`groups-period-badge-option${active ? ' is-selected' : ''}${
                    permanent ? ' is-permanent' : ''
                  }`}
                  onClick={() => {
                    onChange?.(period);
                    setMenuOpen(false);
                  }}
                >
                  <span className="groups-period-badge-option-title">{periodTitle(period)}</span>
                  <span className="groups-period-badge-option-meta">{periodMeta(period)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default GroupsPeriodBadgePicker;
